import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { updateHistoryApi } from '../../api/services';
import { SoftwareUpdateRecord } from '../../types';

// 状态类型定义
interface UpdateHistoryState {
  records: SoftwareUpdateRecord[];
  filteredRecords: SoftwareUpdateRecord[];
  currentSoftware: string | null;
  loading: boolean;
  error: string | null;
}

// 初始状态
const initialState: UpdateHistoryState = {
  records: [],
  filteredRecords: [],
  currentSoftware: null,
  loading: false,
  error: null,
};

// 异步获取所有更新记录
export const fetchAllRecords = createAsyncThunk(
  'updateHistory/fetchAllRecords',
  async (_, { rejectWithValue }) => {
    try {
      const response = await updateHistoryApi.getAllRecords();
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.message || '获取更新记录失败');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取更新记录失败');
    }
  }
);

// 异步获取特定软件的更新记录
export const fetchRecordsBySoftware = createAsyncThunk(
  'updateHistory/fetchRecordsBySoftware',
  async (softwareName: string, { rejectWithValue }) => {
    try {
      const response = await updateHistoryApi.getRecordsBySoftware(softwareName);
      if (response.data.success && response.data.data) {
        return { softwareName, records: response.data.data };
      } else {
        return rejectWithValue(response.data.message || '获取软件更新记录失败');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取软件更新记录失败');
    }
  }
);

// 异步创建新的更新记录
export const createRecord = createAsyncThunk(
  'updateHistory/createRecord',
  async (record: Omit<SoftwareUpdateRecord, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const response = await updateHistoryApi.createRecord(record);
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.message || '创建更新记录失败');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '创建更新记录失败');
    }
  }
);

// 异步更新已有记录
export const updateRecord = createAsyncThunk(
  'updateHistory/updateRecord',
  async ({ id, record }: { id: string; record: Partial<SoftwareUpdateRecord> }, { rejectWithValue }) => {
    try {
      // 确保更新的数据包含所需字段
      const updateData = {
        ...record,
        contentFilePath: record.contentFilePath || '' // 确保有contentFilePath字段
      };
      
      const response = await updateHistoryApi.updateRecord(id, updateData);
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.message || '更新记录失败');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '更新记录失败');
    }
  }
);

// 异步删除记录
export const deleteRecord = createAsyncThunk(
  'updateHistory/deleteRecord',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await updateHistoryApi.deleteRecord(id);
      if (response.data.success) {
        return id;
      } else {
        return rejectWithValue(response.data.message || '删除记录失败');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '删除记录失败');
    }
  }
);

// 更新历史切片
const updateHistorySlice = createSlice({
  name: 'updateHistory',
  initialState,
  reducers: {
    setSoftwareFilter: (state, action: PayloadAction<string | null>) => {
      state.currentSoftware = action.payload;
      if (action.payload) {
        state.filteredRecords = state.records.filter(
          (record) => record.softwareName === action.payload
        );
      } else {
        state.filteredRecords = state.records;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 处理获取所有记录的各种状态
      .addCase(fetchAllRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRecords.fulfilled, (state, action: PayloadAction<SoftwareUpdateRecord[]>) => {
        state.loading = false;
        state.records = action.payload;
        state.filteredRecords = action.payload;
      })
      .addCase(fetchAllRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 处理获取特定软件记录的各种状态
      .addCase(fetchRecordsBySoftware.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecordsBySoftware.fulfilled, (state, action: PayloadAction<{ softwareName: string; records: SoftwareUpdateRecord[] }>) => {
        state.loading = false;
        state.currentSoftware = action.payload.softwareName;
        state.filteredRecords = action.payload.records;
      })
      .addCase(fetchRecordsBySoftware.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 处理创建记录的各种状态
      .addCase(createRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRecord.fulfilled, (state, action: PayloadAction<SoftwareUpdateRecord>) => {
        state.loading = false;
        state.records.push(action.payload);
        // 如果当前没有软件过滤或者与新记录的软件相同，就更新过滤后的记录
        if (!state.currentSoftware || state.currentSoftware === action.payload.softwareName) {
          state.filteredRecords.push(action.payload);
        }
      })
      .addCase(createRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 处理更新记录的各种状态
      .addCase(updateRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRecord.fulfilled, (state, action: PayloadAction<SoftwareUpdateRecord>) => {
        state.loading = false;
        // 更新records数组中的记录
        const index = state.records.findIndex((record) => record.id === action.payload.id);
        if (index !== -1) {
          state.records[index] = action.payload;
        }
        // 更新filteredRecords数组中的记录
        const filteredIndex = state.filteredRecords.findIndex(
          (record) => record.id === action.payload.id
        );
        if (filteredIndex !== -1) {
          state.filteredRecords[filteredIndex] = action.payload;
        }
      })
      .addCase(updateRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 处理删除记录的各种状态
      .addCase(deleteRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRecord.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        // 从records数组中删除记录
        state.records = state.records.filter((record) => record.id !== action.payload);
        // 从filteredRecords数组中删除记录
        state.filteredRecords = state.filteredRecords.filter(
          (record) => record.id !== action.payload
        );
      })
      .addCase(deleteRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSoftwareFilter, clearError } = updateHistorySlice.actions;
export default updateHistorySlice.reducer; 